const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/modules/CIS4m/store.ts';
import Imm from 'immutable';
import { IActionHandlersMap, makeReducer, ReducerState } from '../../defs/actionTypes';
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { CIS4mData, CIS4msMap, CIS4mStoreData, SaveStatus } from './types';
import {
    ActionRemoveCIS4m,
    actionRemoveFromCIS4mDataObject,
    ActionRemoveFromCIS4mDataObject,
    ActionSaveCIS4m,
    ActionSaveCIS4mFailure,
    ActionSaveCIS4mSuccess,
    ActionSetCIS4mDataObject,
    ActionSetDefaultInCIS4mDataObject,
    ActionSetInCIS4mDataObject,
    actionTypes,
    REMOVE_CIS4M,
    REMOVE_FROM_CIS4M_DATA_OBJECT,
    SAVE_CIS4M,
    SAVE_CIS4M_FAILURE,
    SAVE_CIS4M_SUCCESS,
    SET_CIS4M_DATA_OBJECT,
    SET_DEFAULT_IN_CIS4M_DATA_OBJECT,
    SET_IN_CIS4M_DATA_OBJECT,
    SetSaveStatusAction,
    START_CIS4M,
    StartCIS4mAction,
} from './actions';
import { removeEmptyPropertiesFromData } from './utils/data';
import { RequestStatus } from '../../utils/redux4Requests';
import moment from 'moment';
import { KeyValuePair } from '../../defs/generalDefs';
import { createSelector } from 'reselect';
import { CommonStoreState } from '../../reducers';

const KEY_FORMS_DATA = 'forms';
const KEY_REQUEST_STATUS = 'requestStatus';
const KEY_LAST_CHANGED = 'lastChanged';
const KEY_LAST_SAVED = 'lastSaved';
const KEY_DATA_OBJECT = 'dataObject';
const KEY_INITIAL_DATA_OBJECT = 'initialDataObject';

const functionStore: any = {};

const storeFunction = function (func: any): string {
    const uuid = uuidv4();
    functionStore[uuid] = func;
    return uuid;
};

const cleanupFunctionStore = function (formsMapImm: Imm.Map<string, any>) {
    const foundUUIDs: any = {};
    if (formsMapImm) {
        const formsMap = formsMapImm.toJS();
        Object.values(formsMap).forEach((formData: CIS4mStoreData) => {
            foundUUIDs[formData.renderFormFunctionId] = true;
            foundUUIDs[formData.callbackFunctionId] = true;
            foundUUIDs[formData.id] = true;
        });
    }

    Object.keys(functionStore).forEach((funcKey: string) => {
        if (!foundUUIDs[funcKey]) {
            delete functionStore[funcKey];
        }
    });
};

const getStoredFunction = function (funcKey: string): any {
    return functionStore[funcKey];
};

const handleStartCIS4mAction = (state: ReducerState, action: StartCIS4mAction) => {
    const mobileFormConfigs = action.payload.config;

    mobileFormConfigs.forEach((cis4mData: CIS4mData) => {
        const formId = cis4mData.id;
        const form: CIS4mStoreData = {
            id: formId,
            [KEY_DATA_OBJECT]: cis4mData.dataObject,
            [KEY_INITIAL_DATA_OBJECT]: cis4mData.dataObject,
            callbackFunctionId: storeFunction(cis4mData.callback),
            renderFormFunctionId: storeFunction(cis4mData.renderForm),
            [KEY_REQUEST_STATUS]: RequestStatus.PENDING,
            [KEY_LAST_CHANGED]: undefined,
            [KEY_LAST_SAVED]: undefined,
        };
        const formImm = Imm.fromJS(form);
        state = state.setIn([KEY_FORMS_DATA, formId], formImm);
    });

    cleanupFunctionStore(state.get(KEY_FORMS_DATA));

    return state;
};

const updateInitialData = (state: ReducerState, formId: string) => {
    const savedDataObject = state.getIn([KEY_FORMS_DATA, formId, KEY_DATA_OBJECT]);
    state = state.setIn([KEY_FORMS_DATA, formId, KEY_INITIAL_DATA_OBJECT], savedDataObject);
    return state;
};

const handleActionSaveCIS4m = (state: ReducerState, action: ActionSaveCIS4m) => {
    const { formId } = action.payload;
    state = state.setIn([KEY_FORMS_DATA, formId, KEY_REQUEST_STATUS], RequestStatus.LOADING);
    return state;
};

const handleActionSaveCIS4mSuccess = (state: ReducerState, action: ActionSaveCIS4mSuccess) => {
    const { formId } = action.payload;
    state = state.setIn([KEY_FORMS_DATA, formId, KEY_REQUEST_STATUS], RequestStatus.SUCCESS);
    state = updateInitialData(state, formId);
    state = state.setIn([KEY_FORMS_DATA, formId, KEY_LAST_CHANGED], undefined);
    state = state.setIn([KEY_FORMS_DATA, formId, KEY_LAST_SAVED], moment().format());
    return state;
};

const handleActionSaveCIS4mFailed = (state: ReducerState, action: ActionSaveCIS4mFailure) => {
    const { formId } = action.payload;
    state = state.setIn([KEY_FORMS_DATA, formId, KEY_REQUEST_STATUS], RequestStatus.FAILED);
    return state;
};

const updateLastChanged = (state: ReducerState, formId: string) => {
    const lastChangedPath = [KEY_FORMS_DATA, formId, KEY_LAST_CHANGED];
    state = state.setIn(lastChangedPath, moment().format());
    return state;
};

const handleActionSetCIS4mDataObject = function (state: ReducerState, action: ActionSetCIS4mDataObject) {
    const { formId } = action.payload;
    const dataObjectPath = [KEY_FORMS_DATA, formId, KEY_DATA_OBJECT];
    const value = Imm.fromJS(action.payload.formData);
    state = state.setIn(dataObjectPath, value);
    state = updateLastChanged(state, formId);
    return state;
};

const handleActionRemoveFromCIS4mDataObject = function (state: ReducerState, action: ActionRemoveFromCIS4mDataObject) {
    const { formId } = action.payload;

    const keyPath = [KEY_FORMS_DATA, formId, KEY_DATA_OBJECT, ..._.toPath(action.payload.path)];

    if (!state.hasIn(keyPath)) {
        return state;
    }
    state = updateLastChanged(state, formId);
    return state.deleteIn(keyPath);
};

const setInCIS4mDataObject = (state: ReducerState, formId: string, path: string, value: any) => {
    const statePath = [KEY_FORMS_DATA, formId, KEY_DATA_OBJECT, ..._.toPath(path)];
    const valueImm = Imm.fromJS(value);

    if (state.getIn(statePath) === valueImm) {
        return state;
    }

    try {
        state = state.setIn(statePath, valueImm);
    } catch (error) {
        const context = state.getIn([KEY_FORMS_DATA, formId, KEY_DATA_OBJECT]);
        console.error(`Path: "${statePath.join('.')}", value: "${valueImm}", context:`, context, 'error:', error);
        throw error;
    }
    return state;
};

const handleActionSetDefaultInCIS4mDataObject = function (
    state: ReducerState,
    action: ActionSetDefaultInCIS4mDataObject
) {
    const { formId, path, value } = action.payload;

    state = setInCIS4mDataObject(state, formId, path, value);
    state = updateInitialData(state, formId);

    return state;
};

const handleActionSetInCIS4mDataObject = function (state: ReducerState, action: ActionSetInCIS4mDataObject) {
    const { formId, path, value } = action.payload;

    if (_.isUndefined(value)) {
        return handleActionRemoveFromCIS4mDataObject(state, actionRemoveFromCIS4mDataObject(formId, path));
    }

    state = state.withMutations(
        state => (state = updateLastChanged(setInCIS4mDataObject(state, formId, path, value), formId))
    );

    return state;
};

const handleActionRemoveCIS4m = function (state: ReducerState, action: ActionRemoveCIS4m) {
    const { formId } = action.payload;
    state = state.removeIn([KEY_FORMS_DATA, formId]);
    cleanupFunctionStore(state.get(KEY_FORMS_DATA));
    return state;
};

const setSaveStatus = function (state: ReducerState, action: SetSaveStatusAction) {
    return state.set('saveStatus', action.payload.saveStatus);
};

const handlers: IActionHandlersMap = {
    [START_CIS4M]: handleStartCIS4mAction,
    [SET_CIS4M_DATA_OBJECT]: handleActionSetCIS4mDataObject,
    [SET_DEFAULT_IN_CIS4M_DATA_OBJECT]: handleActionSetDefaultInCIS4mDataObject,
    [SET_IN_CIS4M_DATA_OBJECT]: handleActionSetInCIS4mDataObject,
    [REMOVE_FROM_CIS4M_DATA_OBJECT]: handleActionRemoveFromCIS4mDataObject,
    [REMOVE_CIS4M]: handleActionRemoveCIS4m,

    [SAVE_CIS4M]: handleActionSaveCIS4m,
    [SAVE_CIS4M_SUCCESS]: handleActionSaveCIS4mSuccess,
    [SAVE_CIS4M_FAILURE]: handleActionSaveCIS4mFailed,

    [actionTypes.setSaveStatus]: setSaveStatus,
};

export const cis4mReducer = makeReducer(handlers);

// SELECTOR UTILS

const isCIS4mChanged = (formState: CIS4mStoreData) => {
    // we can safely change the objects since they are a deep copy anyway (toJS) and discarded after call of this method
    removeEmptyPropertiesFromData(formState.initialDataObject);
    removeEmptyPropertiesFromData(formState.dataObject);

    // use deep compare instead of stringify since the latter is the death of performance if large images are involved
    return !_.isEqual(formState.initialDataObject, formState.dataObject);
};

const getLastChanged = (formState: CIS4mStoreData) => {
    const isChanged = isCIS4mChanged(formState);
    if (isChanged) {
        return formState.lastChanged;
    }
    return undefined;
};

const prepareCIS4mData = (state: ReducerState, formId: string): CIS4mData | undefined => {
    const formDataImm = state;
    if (formDataImm) {
        const renderForm = getStoredFunction(formDataImm.get('renderFormFunctionId'));
        const callback = getStoredFunction(formDataImm.get('callbackFunctionId'));
        if (renderForm && callback) {
            const cis4mFormStoredData: CIS4mStoreData = formDataImm.toJS();
            return {
                renderForm,
                callback,
                id: cis4mFormStoredData.id,
                [KEY_INITIAL_DATA_OBJECT]: formDataImm.get(KEY_INITIAL_DATA_OBJECT),
                [KEY_DATA_OBJECT]: formDataImm.get(KEY_DATA_OBJECT),
                lastChanged: getLastChanged(cis4mFormStoredData),
                lastSaved: cis4mFormStoredData.lastSaved,
                requestStatus: cis4mFormStoredData.requestStatus,
            };
        }
    }
    return undefined;
};

// SELECTORS

const selectReducerState = (state: CommonStoreState): ReducerState => state.cis4m;

export const selectReducerStateForFormID =
    (formId: string) =>
    (state: CommonStoreState): ReducerState =>
        state.cis4m?.getIn([KEY_FORMS_DATA, formId]);

const makeOrGetMemoizedPrepareCIS4mData = (formId: string) => {
    if (!functionStore[formId]) {
        functionStore[formId] = createSelector([selectReducerStateForFormID(formId)], (state: ReducerState) =>
            prepareCIS4mData(state, formId)
        );
    }
    return functionStore[formId];
};

export const makeSelectCIS4m = (formId: string) => {
    return makeOrGetMemoizedPrepareCIS4mData(formId) as (state: ReducerState) => CIS4mData | undefined;
};

export const makeSelectCIS4MFormData = (formId: string) =>
    createSelector([makeSelectCIS4m(formId)], cis4mData => {
        return cis4mData?.dataObject.toJS();
    });

export const makeSelectCIS4msMap = (argFormIds?: string[]) => {
    return createSelector([selectReducerState], (state: ReducerState) => {
        let formIds = argFormIds; // important! otherwise, we'd change the bound variable

        if (!formIds) {
            const formsData = state.get(KEY_FORMS_DATA);
            if (!formsData) return undefined;
            formIds = Array.from(formsData.keys());
        }

        const cis4msMap: CIS4msMap = formIds.reduce((result: KeyValuePair, formId) => {
            const cis4mData = makeOrGetMemoizedPrepareCIS4mData(formId)({ cis4m: state });
            if (cis4mData) {
                result[formId] = cis4mData;
            }
            return result;
        }, {});

        if (Object.keys(cis4msMap).length === 0) {
            return undefined;
        }
        return cis4msMap;
    });
};

// FIXME: Massnahme stuff
export const selMobileFormSaveStatus = createSelector([selectReducerState], (state: ReducerState): SaveStatus => {
    return state.get('saveStatus');
});
